import React, { Component } from "react";
import Modal from "react-modal";
import PropTypes from "prop-types";
import { Box, DatePicker, Text } from "samba-ui";
import moment from "moment";

import {
  CloseButtonContainer,
  MainContainer,
  InputContainer,
  Header,
  Label,
  Error,
  Input,
  SubmitButton,
  SubmitButtonContainer,
  ContentContainer,
  Description,
  DescriptionWrapper,
  GotItButtonContainer
} from "./styles";

import { RECEIVE_CLIENT } from "../../../actions/client";
import { CloseIcon, ActiveFeedIcon } from "../../../icons/index";

const modalStyles = {
  overlay: {
    display: "flex",
    backgroundColor: "rgba(33,37,48,0.9)",
    zIndex: "1000"
  },
  content: {
    position: "static",
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    width: "500px",
    padding: 0,
    backgroundColor: "transparent",
    border: "none"
  }
};

export const SUCCESS_STEP = "success";

export const CREATE_STEP = "create";

const NAME = "name";

const ABBREVIATION = "abbreviation";

const defaultState = {
  name: "",
  abbreviation: "",
  service_stop: null,
  step: CREATE_STEP,
  loading: false,
  editMode: false
};

// The longest name space we have will be the combination of `development-`
// prefix with `-powered-by-samba` suffix which is 29 characters long, thus
// the name must be no more than 34 characters since AWS requires names
// to be between 3-63 chars total
const MAX_NAME_LENGTH = 34;

class CreateClientModal extends Component {
  state = {
    ...defaultState
  };

  handleDismiss = this.handleDismiss.bind(this);
  handleKeyPress = this.handleKeyPress.bind(this);
  handleSubmit = this.handleSubmit.bind(this);

  componentDidMount() {
    const { client } = this.props;
    this.updateDefaultState(client);
  }

  updateDefaultState(client) {
    if (client) {
      const { name, abbreviation, service_stop } = client;

      this.setState({
        editMode: true,
        name,
        service_stop: moment(service_stop),
        abbreviation
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show === true && this.props.show === false) {
      // If the modal is being closed, we should reset the state
      // Can't do this on componentWillUnmount because technically the modal is always mounted
      this.setState({ ...defaultState });
      this.updateDefaultState(this.props.client);
    }
  }

  getErrorText(input) {
    const { error } = this.props;
    const { name, loading } = this.state;

    if (input === ABBREVIATION) {
      const isAbbreviationError =
        error && error.toLowerCase().includes(ABBREVIATION);
      return !loading && isAbbreviationError && error;
    }

    const lastChar = name.slice(-1);
    const hasIllegalCombo = this.hasConsecutiveDifferentSpecialCharacters();
    const representsIPAddress = name.match(
      /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$/
    );
    const isNameError = error && !error.toLowerCase().includes(ABBREVIATION);

    if (!loading && isNameError && error) return error;
    if (name.length > MAX_NAME_LENGTH) return "Name is too long";
    if (lastChar === ".") return "Can't end in a period";
    if (lastChar === " ") return "Can't end in a space";
    if (lastChar === "-") return "Can't end in a hyphen";
    if (name.includes("..")) return "Can't contain consecutive periods";
    if (hasIllegalCombo) return "Can't use spaces or hyphens beside a period";
    if (representsIPAddress) return "Can't represent an IP address";

    return null;
  }

  hasConsecutiveDifferentSpecialCharacters() {
    const { name } = this.state;
    return (
      name.includes("-.") ||
      name.includes(".-") ||
      name.includes(". ") ||
      name.includes(" .") ||
      name.includes(" -") ||
      name.includes("- ")
    );
  }

  handleDismiss() {
    // We shouldn't close the modal if it's loading
    if (this.state.loading) return;

    this.props.onDismiss();
  }

  handleKeyPress(e) {
    const { name, abbreviation } = this.state;

    if (e.charCode === 13 && name.length > 0 && abbreviation.length > 0) {
      // If the user hits enter while in the input we should submit
      this.handleSubmit();
    }
  }

  handleSubmit() {
    const { name, loading, abbreviation } = this.state;
    const { onSubmit } = this.props;
    if (loading) return;
    this.setState({ loading: true });

    onSubmit({
      name,
      abbreviation,
      service_stop: null,
      service_start: new Date("1970-01-01")
    }).then(action => {
      // Only change the modal to the success message if it was successful
      // but always set loading to false once a response has come back
      if (action.type === RECEIVE_CLIENT) {
        this.setState({ step: SUCCESS_STEP, loading: false });
      } else {
        this.setState({ loading: false });
      }
    });
  }

  isSubmitDisabled() {
    const { abbreviation, loading, name } = this.state;
    if (loading) return true;

    const lastChar = name.slice(-1);
    const isNameIllegal =
      name.length === 0 ||
      name.length > MAX_NAME_LENGTH ||
      lastChar === "." ||
      lastChar === " " ||
      lastChar === "-" ||
      name.includes("..") ||
      this.hasConsecutiveDifferentSpecialCharacters() ||
      !!name.match(/^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$/);
    const isAbbreviationIllegal =
      abbreviation.length < 3 || abbreviation.length > 5;

    return isNameIllegal || isAbbreviationIllegal;
  }

  renderBody() {
    const {
      name,
      step,
      loading,
      abbreviation,
      service_stop,
      editMode
    } = this.state;
    const { error, clearError } = this.props;

    if (step === SUCCESS_STEP) {
      return (
        <ContentContainer>
          <DescriptionWrapper>
            <Description>
              The client
              <span> {name} </span>
              was successfully {editMode ? "updated" : "created"}.
            </Description>
            <ActiveFeedIcon />
            {!editMode ? (
              <Description>
                You may now begin creating feeds for the client
              </Description>
            ) : null}
          </DescriptionWrapper>
          <GotItButtonContainer>
            <SubmitButton onClick={this.handleDismiss} primary>
              Got it
            </SubmitButton>
          </GotItButtonContainer>
        </ContentContainer>
      );
    }

    return (
      <MainContainer>
        <Header>{editMode ? "UPDATE THE CLIENT" : "CREATE A CLIENT"}</Header>
        <InputContainer>
          <Label>
            <div>CLIENT NAME</div>
            <Error>{this.getErrorText(NAME)}</Error>
          </Label>
          <Input
            disabled={editMode}
            data-cy="client-name-input"
            placeholder="Name"
            value={name}
            onPaste={e => {
              // If the error is a name error and we make a change to the
              // name input, we should clear it since that change was made
              const isNameError =
                error && !error.toLowerCase().includes(ABBREVIATION);
              if (isNameError) clearError();

              e.clipboardData.items[0].getAsString(originalText => {
                // Ensure using regex that anything that's not alphanumeric or a
                // legal special character gets replaced with ""
                // The only legal special characters (that satisfy bucket, user,
                // and secrets restrictions) are a period and a dash
                // We'll allow spaces because we want them for the display name
                // and the backend will strip them out and replace them with
                // dashes before calling the lambda function and submitting in AWS
                const textWithOnlyLegalChars = originalText
                  .split("")
                  .map(char => char.replace(/[^a-zA-Z0-9. -]/, ""))
                  .join("");

                this.setState({
                  name: textWithOnlyLegalChars
                });
              });
            }}
            onChange={e => {
              // If the error is a name error and we make a change to the
              // name input, we should clear it since that change was made
              const isNameError =
                error && !error.toLowerCase().includes(ABBREVIATION);
              if (isNameError) clearError();

              // Ensure using regex that anything that's not alphanumeric or a
              // legal special character gets replaced with ""
              // The only legal special characters (that satisfy bucket, user,
              // and secrets restrictions) are a period and a dash
              // We'll allow spaces because we want them for the display name
              // and the backend will strip them out and replace them with
              // dashes before calling the lambda function and submitting in AWS
              const name = e.target.value.replace(/[^a-zA-Z0-9. -]/, "");
              this.setState({ name });
            }}
            onKeyPress={this.handleKeyPress}
          />
        </InputContainer>
        <InputContainer>
          <Label>
            <div>CLIENT ABBR. (3-5 chars)</div>
            <Error>{this.getErrorText(ABBREVIATION)}</Error>
          </Label>
          <Input
            disabled={editMode}
            onKeyPress={this.handleKeyPress}
            data-cy="client-abbreviation-input"
            placeholder="Abbreviation used in feed names"
            value={abbreviation}
            onPaste={e => {
              // If the error is an abbreviation error and we make a change to the
              // abbreviation input, we should clear it since that change was made
              const isAbbreviationError =
                error && error.toLowerCase().includes(ABBREVIATION);
              if (isAbbreviationError) clearError();

              e.clipboardData.items[0].getAsString(originalText => {
                // Ensure using regex that anything that's not a letter gets replaced with ""
                const textWithOnlyLegalChars = originalText
                  .split("")
                  .map(char => char.replace(/[^a-zA-Z]/, ""))
                  .join("");

                // Ensure that we capitalize the letters and take a maximum of 5 characters
                this.setState({
                  abbreviation: textWithOnlyLegalChars.toUpperCase().slice(0, 5)
                });
              });
            }}
            onChange={e => {
              // If the error is an abbreviation error and we make a change to the
              // abbreviation input, we should clear it since that change was made
              const isAbbreviationError =
                error && error.toLowerCase().includes(ABBREVIATION);
              if (isAbbreviationError) clearError();

              // Ensure using regex that anything that's not a letter gets replaced with ""
              const value = e.target.value.replace(/[^a-zA-Z]/, "");
              // Ensure that we capitalize the letters and take a maximum of 5 characters
              this.setState({ abbreviation: value.toUpperCase().slice(0, 5) });
            }}
          />
        </InputContainer>
        <SubmitButtonContainer>
          <SubmitButton
            data-cy="create-client-submit"
            disabled={this.isSubmitDisabled()}
            onClick={this.handleSubmit}
          >
            {loading
              ? "Processing..."
              : editMode
              ? "Update the client"
              : "Create a client"}
          </SubmitButton>
        </SubmitButtonContainer>
      </MainContainer>
    );
  }

  render() {
    const { show, shouldCloseOnOverlayClick } = this.props;

    return (
      <Modal
        isOpen={show}
        onRequestClose={this.handleDismiss}
        style={modalStyles}
        ariaHideApp={false}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      >
        <CloseButtonContainer onClick={this.handleDismiss}>
          <CloseIcon />
        </CloseButtonContainer>
        {this.renderBody()}
      </Modal>
    );
  }
}

CreateClientModal.defaultProps = {
  shouldCloseOnOverlayClick: true
};

CreateClientModal.propTypes = {
  show: PropTypes.bool,
  onDismiss: PropTypes.func,
  shouldCloseOnOverlayClick: PropTypes.bool,
  onSubmit: PropTypes.func,
  error: PropTypes.string,
  clearError: PropTypes.func,
  client: PropTypes.object
};

export default CreateClientModal;
